import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useSearch } from '@tanstack/react-router';
import { acceptInviteSchema, type AcceptInviteInput } from '@ddp/shared';
import Button from '@carbon/react/es/components/Button';
import Form from '@carbon/react/es/components/Form/Form';
import { Stack } from '@carbon/react/es/components/Stack/Stack';
import TextInput from '@carbon/react/es/components/TextInput';
import PasswordInput from '@carbon/react/es/components/TextInput/PasswordInput';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';

import { useAuthStore } from '../auth/store.js';
import { zodResolver } from '../lib/zod-resolver.js';
import { AuthCard } from './AuthCard.js';

/**
 * Page publique d'acceptation d'une invitation. Le jeton arrive dans l'URL
 * (`/invitations/accept?token=…`, cf. `InvitationsService`). L'invité choisit son nom affiché et
 * son mot de passe ; le rôle et l'organisation sont portés par le jeton, jamais saisis ici. Le
 * back refuse un jeton expiré, révoqué ou déjà utilisé (message renvoyé tel quel).
 */
export function AcceptInvitePage() {
  const navigate = useNavigate();
  const { token } = useSearch({ from: '/invitations/accept' });
  const acceptInvite = useAuthStore((state) => state.acceptInvite);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AcceptInviteInput>({
    resolver: zodResolver(acceptInviteSchema),
    defaultValues: { token: token ?? '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await acceptInvite(values);
      await navigate({ to: '/' });
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Acceptation impossible.');
    }
  });

  // Sans jeton, le lien est incomplet : inutile d'afficher le formulaire, il échouerait.
  if (!token) {
    return (
      <AuthCard
        title="Invitation invalide"
        footer={
          <>
            Déjà un compte ? <RouterLink to="/login">Se connecter</RouterLink>
          </>
        }
      >
        <InlineNotification
          kind="error"
          title="Lien incomplet"
          subtitle="Ce lien d’invitation ne contient pas de jeton. Demandez à l’administrateur de vous le renvoyer."
          lowContrast
          hideCloseButton
        />
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Rejoindre l’organisation"
      footer={
        <>
          Déjà un compte ? <RouterLink to="/login">Se connecter</RouterLink>
        </>
      }
    >
      <Form onSubmit={onSubmit}>
        <Stack gap={6}>
          <p className="ddp-auth__lead">
            Vous avez été invité·e à rejoindre une organisation. Choisissez un nom affiché et un mot
            de passe pour créer votre compte.
          </p>
          {formError && (
            <InlineNotification
              kind="error"
              title="Échec de l’acceptation"
              subtitle={formError}
              lowContrast
              hideCloseButton
            />
          )}
          <TextInput
            id="displayName"
            labelText="Nom affiché"
            invalid={Boolean(errors.displayName)}
            invalidText={errors.displayName?.message}
            {...register('displayName')}
          />
          <PasswordInput
            id="password"
            labelText="Mot de passe"
            autoComplete="new-password"
            helperText="12 caractères minimum, avec majuscule, minuscule et chiffre."
            showPasswordLabel="Afficher le mot de passe"
            hidePasswordLabel="Masquer le mot de passe"
            invalid={Boolean(errors.password)}
            invalidText={errors.password?.message}
            {...register('password')}
          />
          <Button type="submit" disabled={isSubmitting}>
            Créer le compte et rejoindre
          </Button>
        </Stack>
      </Form>
    </AuthCard>
  );
}
